import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { ArrowLeft, MapPin, Home, Key } from "lucide-react";
import Layout from "../../components/Layout";
import Loading from "../../components/Loading";
import PropertyChildrenSection from "../../components/PropertyChildrenSection";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import formatValue from "../../utils/formatValue";
import { normalizeKeyLocationLabel } from "../../services/propertyKeyLocation";
import { extractParentPropertyId } from "../../utils/resolvePropertyNavigationId";
import {
  formatPropertyStatusLabel,
  isDraftStatus,
} from "../../global/propertyStatus";
import { formatHousingProgramLabel } from "../../utils/housingProgram";
import PropertyCommissionSummary from "../../components/PropertyCommissionSummary";

interface IParams {
  id: string;
}

interface IApiResponse {
  property: {
    id: number;
    title: string;
    status_id: number;
    consultant_id: number;
    rent_value: string;
    condominium_value: string;
    iptu_value: string;
    /** Flattened complement when API omits nested address.floor */
    floor?: string | null;
    kenlo_property_id: string | null;
    housing_program?: "HIS" | "HMP" | null;
    status?: {
      id: number;
      name: string;
    };
    address?: {
      id?: number;
      address: string;
      number: string;
      complement?: string | null;
      floor?: string | null;
      neighborhood: string;
      city: string;
      uf: string;
    };
    photos?: {
      id: number;
      url: string;
      order: number;
    }[];
    consultant?: {
      id: number;
      first_name: string;
      email: string;
      phone: string;
    } | null;
    owner?: {
      id: number;
      first_name: string;
      email: string;
      phone: string;
    } | null;
    /** API may send string or `{ name: string }` */
    key_location?: unknown;
    key_location_note?: string | null;
  };
  kenlo_contract: {
    contract_id: string | null;
    property_id: string | null;
    code: string | null;
    property: {
      type: string | null;
      description: string | null;
      condominium: string | null;
      internal_code: string | null;
      address: {
        street: string | null;
        number: string | null;
        complement: string | null;
        district: string | null;
        city: string | null;
        state: string | null;
        zip_code: string | null;
      };
    };
    tenants: Array<{
      id: string | null;
      name: string | null;
      email: string | null;
      phone: string | null;
      is_main: boolean;
      fraction: string | null;
    }>;
    owners: Array<{
      id: string | null;
      name: string | null;
      email: string | null;
      phone: string | null;
      is_main: boolean;
      fraction: string | null;
    }>;
    main_tenant: {
      id: string | null;
      name: string | null;
      email: string | null;
      phone: string | null;
      is_main: boolean;
      fraction: string | null;
    } | null;
    main_owner: {
      id: string | null;
      name: string | null;
      email: string | null;
      phone: string | null;
      is_main: boolean;
      fraction: string | null;
    } | null;
    term: {
      start_date: string | null;
      end_date: string | null;
      start_date_iso: string | null;
      end_date_iso: string | null;
      duration_months: number | null;
    };
    rent: {
      amount: string | null;
      administration_fee: string | null;
      due_day: string | null;
    };
    status: {
      code: string | null;
      label: string | null;
    };
    owner_summary: {
      name: string | null;
    };
    raw?: {
      updated_at?: string | null;
      occupation_date?: string | null;
      resignation_date?: string | null;
    };
  } | null;
  kenlo_message?: string | null;
  has_kenlo_contract: boolean;
}

const MyPropertyDetails: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { params } = useRouteMatch<IParams>();
  const { access_token, dateToExpires, user } = useAuth();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IApiResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    async function loadApi() {
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        const requestedId = Number(params.id);
        const response = await api.get(
          `/api/adm/properties/${requestedId}/details`
        );
        const payload = response.data.data ?? response.data;
        const parentId = extractParentPropertyId({ data: payload });
        if (parentId != null && parentId !== requestedId) {
          history.replace(
            `/property/${parentId}/details${location.search || ""}`
          );
          return;
        }

        setData(payload);
        setErrorMessage("");
      } catch (err: any) {
        const message =
          err?.response?.data?.message ?? "Erro ao carregar detalhes do imóvel";

        setErrorMessage(message);

        addToast({
          title: message,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }

    if (user) {
      loadApi();
    } else {
      history.push("/");
    }
  }, [
    access_token,
    dateToExpires,
    params.id,
    user,
    history,
    location.search,
    addToast,
  ]);

  const property = data?.property;
  const kenloContract = data?.kenlo_contract;

  const fullAddress = useMemo(() => {
    if (!property?.address) return "-";

    const { address, number, complement, neighborhood, city, uf } = property.address;
    const floor =
      property.address.floor ?? property.floor ?? null;

    return [address, number, complement, floor, neighborhood, `${city}/${uf}`]
      .filter((part) => part != null && String(part).trim() !== "")
      .join(", ");
  }, [property]);

  const keyLocationLabel = useMemo(
    () => normalizeKeyLocationLabel(property?.key_location),
    [property?.key_location]
  );

  const keyLocationNote = useMemo(() => {
    const n = property?.key_location_note;
    if (n == null || String(n).trim() === "") return null;
    return String(n).trim();
  }, [property?.key_location_note]);

  const kenloAddress = useMemo(() => {
    if (!kenloContract?.property?.address) return "-";

    const { street, number, complement, district, city, state, zip_code } =
      kenloContract.property.address;

    return [street, number, complement, district, city, state, zip_code]
      .filter(Boolean)
      .join(", ");
  }, [kenloContract]);

  const totalValue = useMemo(() => {
    const rent = Number(property?.rent_value ?? 0);
    const condominium = Number(property?.condominium_value ?? 0);
    const iptu = Number(property?.iptu_value ?? 0);

    return formatValue(rent + condominium + iptu);
  }, [property]);

  const housingProgramLabel = useMemo(
    () => formatHousingProgramLabel(property?.housing_program),
    [property?.housing_program]
  );

  const mainTenant = useMemo(() => {
    if (!kenloContract?.tenants?.length) return null;

    return (
      kenloContract.tenants.find((tenant) => tenant.is_main) ??
      kenloContract.tenants[0]
    );
  }, [kenloContract]);

  function getStatusClasses(status?: string) {
    const normalized = (status ?? "").toLowerCase();

    if (isDraftStatus(status)) {
      return "border border-amber-200 bg-amber-50 text-amber-800";
    }

    if (normalized.includes("alug")) {
      return "border border-emerald-200 bg-emerald-50 text-emerald-700";
    }

    if (normalized.includes("public")) {
      return "border border-slate-200 bg-slate-100 text-slate-600";
    }

    if (normalized.includes("desativ") || normalized.includes("inativ")) {
      return "border border-red-200 bg-red-50 text-red-700";
    }

    return "border border-orange-200 bg-orange-50 text-orange-700";
  }

  function infoItem(label: string, value?: React.ReactNode) {
    return (
      <div className="flex flex-col gap-1.5">
        <strong className="text-[13px] font-semibold text-[#666666]">{label}</strong>
        <span className="break-words text-[15px] leading-6 text-[#2d2d2d]">
          {value ?? "-"}
        </span>
      </div>
    );
  }

  return (
    <Layout>
      <div className="w-full">
        {loading && <Loading />}

        {!loading && (
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:py-10">
            {!!errorMessage && (
              <div className="px-4 py-10">
                <h2 className="text-center text-2xl font-medium text-[#666666]">
                  {errorMessage}
                </h2>
              </div>
            )}

            {!errorMessage && property && (
              <>
                <div className="mb-7 flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                  <div className="flex flex-col gap-2.5">
                    <button
                      type="button"
                      onClick={() => history.goBack()}
                      className="inline-flex w-fit items-center gap-2 border-0 bg-transparent text-[15px] font-semibold text-[#dd6435] transition hover:opacity-90"
                    >
                      <ArrowLeft className="h-[18px] w-[18px]" />
                      Voltar
                    </button>

                    <h1 className="text-[26px] font-bold leading-tight text-[#2d2d2d] md:text-[32px]">
                      {property.title}
                    </h1>

                    <p className="text-[15px] text-[#7a7a7a]">Imóvel #{property.id}</p>
                  </div>

                  <div className="flex min-w-[220px] flex-col gap-2.5 md:items-end">
                    <span className="text-sm text-[#666666]">Situação do imóvel</span>

                    <span
                      className={`inline-flex min-h-[34px] w-fit items-center justify-center rounded-full px-[14px] py-[7px] text-[13px] font-semibold ${getStatusClasses(
                        property.status?.name
                      )}`}
                    >
                      {formatPropertyStatusLabel(property.status?.name)}
                    </span>

                    {housingProgramLabel ? (
                      <span className="inline-flex min-h-[34px] w-fit items-center justify-center rounded-full border border-sky-200 bg-sky-50 px-[14px] py-[7px] text-[13px] font-semibold text-sky-800">
                        {housingProgramLabel}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-[1.2fr_1fr]">
                  <section className="rounded-2xl border border-[#e2e2e2] bg-gradient-to-b from-white to-[#fafafa] p-6 shadow-[0_6px_22px_rgba(0,0,0,0.055)]">
                    <h2 className="mb-5 flex items-center gap-2.5 text-xl font-bold text-[#2d2d2d]">
                      <Home className="h-[18px] w-[18px]" />
                      Resumo financeiro
                    </h2>

                    <div className="grid grid-cols-1 gap-[14px] sm:grid-cols-2">
                      <div className="rounded-xl border border-[#e6e6e6] bg-white p-4">
                        <strong className="mb-2 block text-[13px] font-semibold text-[#666666]">
                          Aluguel
                        </strong>
                        <span className="text-lg font-bold text-[#2d2d2d]">
                          {formatValue(Number(property.rent_value ?? 0))}
                        </span>
                      </div>

                      <div className="rounded-xl border border-[#e6e6e6] bg-white p-4">
                        <strong className="mb-2 block text-[13px] font-semibold text-[#666666]">
                          Condomínio
                        </strong>
                        <span className="text-lg font-bold text-[#2d2d2d]">
                          {formatValue(Number(property.condominium_value ?? 0))}
                        </span>
                      </div>

                      <div className="rounded-xl border border-[#e6e6e6] bg-white p-4">
                        <strong className="mb-2 block text-[13px] font-semibold text-[#666666]">
                          IPTU
                        </strong>
                        <span className="text-lg font-bold text-[#2d2d2d]">
                          {formatValue(Number(property.iptu_value ?? 0))}
                        </span>
                      </div>

                      <div className="rounded-xl border border-[#e8c9b8] bg-[#fff8f4] p-4">
                        <strong className="mb-2 block text-[13px] font-semibold text-[#8a5a44]">
                          Total mensal
                        </strong>
                        <span className="text-lg font-bold text-[#8a5a44]">
                          {totalValue}
                        </span>
                      </div>
                    </div>
                  </section>

                  <section className="rounded-2xl border border-[#e2e2e2] bg-gradient-to-b from-white to-[#fafafa] p-6 shadow-[0_6px_22px_rgba(0,0,0,0.055)]">
                    <h2 className="mb-5 flex items-center gap-2.5 text-xl font-bold text-[#2d2d2d]">
                      <MapPin className="h-[18px] w-[18px]" />
                      Localização
                    </h2>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-6">
                      <div className="md:col-span-2">
                        {infoItem("Endereço completo", fullAddress)}
                      </div>

                      {housingProgramLabel ? (
                        <div className="md:col-span-2">
                          {infoItem("Enquadramento habitacional", housingProgramLabel)}
                        </div>
                      ) : null}

                      <div>{infoItem("Bairro", property.address?.neighborhood)}</div>
                      <div>{infoItem("Cidade", property.address?.city)}</div>
                      <div>{infoItem("UF", property.address?.uf)}</div>
                    </div>
                  </section>
                </div>

                <section className="mb-5 w-full rounded-2xl border border-[#e2e2e2] bg-gradient-to-b from-white to-[#fafafa] p-6 shadow-[0_6px_22px_rgba(0,0,0,0.055)]">
                  <h2 className="mb-5 flex items-center gap-2.5 text-xl font-bold text-[#2d2d2d]">
                    <Key className="h-[18px] w-[18px]" aria-hidden />
                    Local das chaves
                  </h2>
                  <p className="break-words text-[15px] leading-6 text-[#2d2d2d]">
                    {keyLocationLabel !== "" ? keyLocationLabel : "—"}
                  </p>
                  {keyLocationNote ? (
                    <div className="mt-4">
                      {infoItem("Observação", keyLocationNote)}
                    </div>
                  ) : null}
                </section>

                <div className="mb-5">
                  <PropertyCommissionSummary
                    property={property as unknown as Record<string, unknown>}
                  />
                </div>

                <div className="mb-5">
                  <PropertyChildrenSection
                    mode="manage"
                    propertyId={property.id}
                    allowStatusActions
                  />
                </div>

                <div className="flex flex-col gap-5">
                  <section className="w-full rounded-2xl border border-[#e2e2e2] bg-white p-6 shadow-[0_6px_22px_rgba(0,0,0,0.055)]">
                    <h2 className="mb-5 text-[21px] font-bold text-[#2d2d2d]">
                      Galeria de fotos
                    </h2>

                    {property.photos?.length ? (
                      <div className="grid grid-cols-2 gap-[14px] md:grid-cols-3 xl:grid-cols-4">
                        {[...property.photos]
                          .sort((a, b) => a.order - b.order)
                          .map((photo) => (
                            <div
                              key={photo.id}
                              className="h-[150px] overflow-hidden rounded-[14px] border border-[#e0e0e0] bg-[#f3f3f3] shadow-[0_2px_8px_rgba(0,0,0,0.045)] md:h-[220px]"
                            >
                              <img
                                src={photo.url}
                                alt={`Foto ${photo.order} do imóvel`}
                                className="block h-full w-full object-cover"
                              />
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="rounded-[14px] border border-[#e8c9b8] bg-[#fff8f4] p-[18px]">
                        <p className="text-[15px] leading-6 text-[#8a5a44]">
                          Nenhuma foto cadastrada para este imóvel.
                        </p>
                      </div>
                    )}
                  </section>

                  <section className="w-full rounded-2xl border border-[#e2e2e2] bg-white p-6 shadow-[0_6px_22px_rgba(0,0,0,0.055)]">
                    <h2 className="mb-5 text-[21px] font-bold text-[#2d2d2d]">
                      Proprietário e inquilino
                    </h2>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="rounded-[14px] border border-[#e8e8e8] bg-[#fafafa] p-[18px]">
                        <h3 className="mb-[14px] text-[17px] font-bold text-[#2d2d2d]">
                          Proprietário
                        </h3>

                        <div className="space-y-3">
                          {infoItem("Nome", property.owner?.first_name)}
                          {infoItem("Email", property.owner?.email)}
                          {infoItem("Telefone", property.owner?.phone)}
                        </div>
                      </div>

                      <div className="rounded-[14px] border border-[#e8e8e8] bg-[#fafafa] p-[18px]">
                        <h3 className="mb-[14px] text-[17px] font-bold text-[#2d2d2d]">
                          Inquilino
                        </h3>

                        <div className="space-y-3">
                          {infoItem("Nome", mainTenant?.name)}
                          {infoItem("Email", mainTenant?.email)}
                          {infoItem("Telefone", mainTenant?.phone)}
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="w-full rounded-2xl border border-[#e2e2e2] bg-white p-6 shadow-[0_6px_22px_rgba(0,0,0,0.055)]">
                    <h2 className="mb-5 text-[21px] font-bold text-[#2d2d2d]">
                      Contrato e financeiro Kenlo
                    </h2>

                    {data?.has_kenlo_contract && kenloContract ? (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-6">
                        <div className="rounded-[14px] border border-[#e8e8e8] bg-[#fafafa] p-[18px]">
                          <h3 className="mb-[14px] text-[17px] font-bold text-[#2d2d2d]">
                            Contrato
                          </h3>

                          <div className="space-y-3">
                            {infoItem("Código do contrato", kenloContract.code)}
                            {infoItem("Status", kenloContract.status?.label)}
                            {infoItem("Início", kenloContract.term?.start_date)}
                            {infoItem("Fim", kenloContract.term?.end_date)}
                            {infoItem(
                              "Duração",
                              kenloContract.term?.duration_months != null
                                ? `${kenloContract.term.duration_months} meses`
                                : "-"
                            )}
                          </div>
                        </div>

                        <div className="rounded-[14px] border border-[#e8e8e8] bg-[#fafafa] p-[18px]">
                          <h3 className="mb-[14px] text-[17px] font-bold text-[#2d2d2d]">
                            Financeiro
                          </h3>

                          <div className="space-y-3">
                            {infoItem(
                              "Aluguel",
                              kenloContract.rent?.amount
                                ? formatValue(Number(kenloContract.rent.amount))
                                : "-"
                            )}
                            {infoItem(
                              "Taxa administrativa",
                              kenloContract.rent?.administration_fee
                                ? `${kenloContract.rent.administration_fee}%`
                                : "-"
                            )}
                            {infoItem("Dia do vencimento", kenloContract.rent?.due_day)}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-[14px] border border-[#e8c9b8] bg-[#fff8f4] p-[18px]">
                        <p className="text-[15px] leading-6 text-[#8a5a44]">
                          {data?.kenlo_message ??
                            "Contrato ainda não sincronizado ou inexistente na plataforma financeira."}
                        </p>
                      </div>
                    )}
                  </section>

                  {data?.has_kenlo_contract && kenloContract && (
                    <section className="w-full rounded-2xl border border-[#e2e2e2] bg-white p-6 shadow-[0_6px_22px_rgba(0,0,0,0.055)]">
                      <h2 className="mb-5 text-[21px] font-bold text-[#2d2d2d]">
                        Dados do imóvel no Kenlo
                      </h2>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-6">
                        <div>{infoItem("Tipo", kenloContract.property?.type)}</div>
                        <div>
                          {infoItem("Descrição", kenloContract.property?.description)}
                        </div>
                        <div>
                          {infoItem("Condomínio", kenloContract.property?.condominium)}
                        </div>
                        <div>
                          {infoItem(
                            "Código interno",
                            kenloContract.property?.internal_code
                          )}
                        </div>
                        <div className="md:col-span-2">
                          {infoItem("Endereço no Kenlo", kenloAddress)}
                        </div>
                        <div>
                          {infoItem("ID Kenlo do imóvel", property.kenlo_property_id)}
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyPropertyDetails;